import { post, store, type Session, type Tokens } from "./api.ts";
import { creationOptions, credentialJSON, requestOptions } from "./webauthn.ts";

interface Challenge { challengeId: string; options: any }

function device(): string {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua)) return "iPad";
  if (/Android/.test(ua)) return "Android";
  if (/Mac/.test(ua)) return "Mac";
  if (/Windows/.test(ua)) return "Windows";
  return "Browser";
}

function keep(s: Session): Session {
  store.refresh = s.tokens.refreshToken;
  return s;
}

export async function signUp(displayName: string): Promise<Session> {
  const ch = await post<Challenge>("/v1/signup/begin", { displayName });
  const cred = (await navigator.credentials.create(creationOptions(ch.options))) as PublicKeyCredential;
  return keep(await post<Session>("/v1/signup/finish", { challengeId: ch.challengeId, credential: credentialJSON(cred), device: device() }));
}

export async function signIn(): Promise<Session> {
  const ch = await post<Challenge>("/v1/login/begin", {});
  const cred = (await navigator.credentials.get(requestOptions(ch.options))) as PublicKeyCredential;
  return keep(await post<Session>("/v1/login/finish", { challengeId: ch.challengeId, credential: credentialJSON(cred), device: device() }));
}

/** Resumes a session from the stored refresh token (rotated on every use). */
export async function resume(): Promise<Tokens | null> {
  const refreshToken = store.refresh;
  if (!refreshToken) return null;
  try {
    const { tokens } = await post<{ tokens: Tokens }>("/v1/refresh", { refreshToken });
    store.refresh = tokens.refreshToken;
    return tokens;
  } catch {
    store.refresh = null;
    return null;
  }
}

export async function signOut(accessToken: string, everywhere = false): Promise<void> {
  try { await post(everywhere ? "/v1/logout-all" : "/v1/logout", {}, accessToken); } finally { store.refresh = null; }
}

/** The user ID ("sub") from an access token, for display only (the server verifies). */
export function subject(accessToken: string): string {
  try { return JSON.parse(atob(accessToken.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))).sub ?? ""; } catch { return ""; }
}
